import { useAtomValue } from 'jotai';

import {
	ActionMenu,
	ActionMenuItem,
	ActionMenuTrigger,
	Box,
	Button,
	Divider,
	Group,
	Stack,
	toast,
} from '@ekhodealer/ekho-common/rev';

import type { LeadDetails } from '@ekhodealer/ekho-common';

import { useGetLeadDetails } from '@common/api/leads/useGetLeadDetails';
import { AccordionSection } from '@common/components/AccordionSection';
import { ChevronDown12, Eye24 } from '@common/components/icons';
import Link24 from '@common/components/icons/Link24';
import { InfoLineItem } from '@common/components/InfoLineItem';

import { leadDetailsDrawerAtom } from '../../atoms/leadDetailsDrawerAtoms';

import { LeadsVehicle } from './shared/LeadsVehicle';

const SECTION_TITLE = 'Abandoned carts';

interface AbandonedCartProps {
	cart: LeadDetails['abandonedCarts'][number];
}

/**
 * Individual abandoned cart display component
 * Shows vehicle details, abandonment info, and actions
 */
const AbandonedCart = ({ cart }: AbandonedCartProps) => {
	const { vehicle, dateAbandoned, lastViewed, timesViewed } = cart;

	return (
		<Box rounding='12' background='secondary' padding='16'>
			<Stack itemsSpacing='12'>
				{/* Vehicle Info Box */}
				<LeadsVehicle vehicle={vehicle} />

				{/* Cart Details Box */}
				<Box rounding='12' padding='12' background='primary'>
					<Stack itemsSpacing='12'>
						<InfoLineItem title='Date abandoned' value={dateAbandoned} />
						<InfoLineItem title='Last viewed' value={lastViewed} />
						<InfoLineItem title='Times viewed' value={timesViewed} />
					</Stack>
				</Box>

				{/* Action Buttons */}
				<Group itemsAlignX='space-between' itemsSpacing='12'>
					<ActionMenuTrigger>
						<Button
							variant='secondary'
							size='medium'
							icon={<ChevronDown12 />}
							iconSide='right'>
							More
						</Button>
						<ActionMenu>
							{cart.previewUrl && (
								<ActionMenuItem
									label='Preview abandoned cart'
									leadingAccessory={<Eye24 />}
									onAction={() => {
										if (cart.previewUrl) {
											window.open(cart.previewUrl, '_blank');
										}
									}}
								/>
							)}
							{cart.shareableUrl && (
								<ActionMenuItem
									label='Copy link'
									leadingAccessory={<Link24 />}
									onAction={() => {
										if (cart.shareableUrl) {
											navigator.clipboard.writeText(cart.shareableUrl);
											toast({
												message: 'Link copied to clipboard',
												variant: 'success',
											});
										}
									}}
								/>
							)}
						</ActionMenu>
					</ActionMenuTrigger>
				</Group>
			</Stack>
		</Box>
	);
};

export const AbandonedCartsSection = () => {
	const { selectedRow } = useAtomValue(leadDetailsDrawerAtom);
	const { data: leadDetails } = useGetLeadDetails(selectedRow?.id);

	if (!leadDetails || !selectedRow?.id) {
		return null;
	}

	const { abandonedCarts } = leadDetails;

	// Hide section if no abandoned carts
	if (abandonedCarts.length === 0) {
		return null;
	}

	const sectionTitle = `${SECTION_TITLE} (${abandonedCarts.length})`;

	return (
		<>
			<Divider />
			<AccordionSection title={sectionTitle} defaultExpanded>
				<Stack itemsSpacing='12'>
					{abandonedCarts.map((cart, index) => (
						<AbandonedCart key={index} cart={cart} />
					))}
				</Stack>
			</AccordionSection>
		</>
	);
};
